import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { MESSAGE_CONSTANTS } from '../../../../../shared/constants/message';
import {
  isAtScrollTop,
  isNearBottom,
  preserveScrollAfterPrepend,
} from '../../../../../shared/utils/chatUtil';

const TOP_PX = MESSAGE_CONSTANTS.SCROLL_TOP_THRESHOLD_PX;
/** Bound empty short-thread probes (raw-event pages that add no UI rows). */
const SHORT_THREAD_FILL_ATTEMPTS = 8;
/** Bound at-top retries when a page yields no rows but the server still has history. */
const TOP_HISTORY_RETRY_ATTEMPTS = 8;
/** Minimum time the history loader stays visible (avoids invisible flash loads). */
const MIN_HISTORY_LOADER_MS = 280;

const listOverflows = (container) =>
  Boolean(container) && container.scrollHeight > container.clientHeight + 1;

const waitMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Scroll model for 1:1 Matrix chat (live timeline: oldest → newest, newest at bottom).
 *
 * - Opening a thread scrolls to the latest messages.
 * - Height-delta anchoring after older rows appear; only manual scroll moves the viewport.
 * - IntersectionObserver + scroll-top band trigger pagination when more history exists.
 * - `isPagingUp` keeps the loader visible for at least MIN_HISTORY_LOADER_MS.
 */
export function useMatrixOneToOneChatScroll({
  chatWrapperRef,
  historyTopRef,
  roomId,
  roomMessages,
  lastVisibleMessageKey,
  loadingMessages,
  isChatReady,
  isMessageListPainted,
  loadingOlder,
  hasMoreBackwards,
  loadOlderMessages,
  markAsReadManually,
}) {
  const hasDoneInitialScrollRef = useRef(false);
  const lastScrollRoomIdRef = useRef(null);
  const forceScrollToBottomRef = useRef(false);
  const scrollRafRef = useRef(null);
  const loadOlderInFlightRef = useRef(false);
  const requestGenRef = useRef(0);
  const shortProbeCountRef = useRef(0);
  const topHistoryRetryCountRef = useRef(0);
  const hasMoreBackwardsRef = useRef(hasMoreBackwards);
  const [olderLoadTick, setOlderLoadTick] = useState(0);
  const [isPagingUp, setIsPagingUp] = useState(false);

  const historyAnchorMetricsRef = useRef(null);
  const pendingHistoryAnchorRef = useRef(false);

  const stickToLatestUntilUserScrollRef = useRef(true);
  const nearBottomRef = useRef(true);
  const prevListLayoutRef = useRef({ length: 0, firstKey: null });
  const resizeObsHeightRef = useRef(0);
  const initialScrollRafRef = useRef(null);
  const initialScrollGenRef = useRef(0);

  const cancelPendingInitialScroll = useCallback(() => {
    initialScrollGenRef.current += 1;
    if (initialScrollRafRef.current != null) {
      window.cancelAnimationFrame(initialScrollRafRef.current);
      initialScrollRafRef.current = null;
    }
  }, []);

  useEffect(() => {
    hasMoreBackwardsRef.current = hasMoreBackwards;
  }, [hasMoreBackwards]);

  const messagesLayoutFingerprint = useMemo(() => {
    if (!roomMessages.length) return '0';
    const first = roomMessages[0];
    const last = roomMessages[roomMessages.length - 1];
    const fk = first?.eventId || first?.id || String(first?.ts ?? '');
    const lk = last?.eventId || last?.id || String(last?.ts ?? '');
    return `${roomMessages.length}:${fk}:${lk}`;
  }, [roomMessages]);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const container = chatWrapperRef.current;
    if (!container) return;
    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    });
  }, [chatWrapperRef]);

  const saveHistoryAnchor = useCallback((container) => {
    stickToLatestUntilUserScrollRef.current = false;
    nearBottomRef.current = false;
    historyAnchorMetricsRef.current = {
      scrollHeight: container.scrollHeight,
      scrollTop: container.scrollTop,
    };
    pendingHistoryAnchorRef.current = true;
  }, []);

  const requestOlderMessages = useCallback(() => {
    if (!hasDoneInitialScrollRef.current) return false;
    if (loadOlderInFlightRef.current || loadingOlder || isPagingUp) return false;
    if (!hasMoreBackwardsRef.current) return false;

    const container = chatWrapperRef.current;
    if (!container) return false;

    const overflowing = listOverflows(container);
    if (overflowing && isNearBottom(container)) return false;

    saveHistoryAnchor(container);

    const gen = requestGenRef.current;
    loadOlderInFlightRef.current = true;
    setIsPagingUp(true);
    const startedAt = Date.now();

    Promise.resolve(loadOlderMessages?.(MESSAGE_CONSTANTS.LOAD_OLDER_PAGE_SIZE))
      .then((result) => {
        if (gen !== requestGenRef.current) return;
        const loaded = Number(result?.loaded) > 0;
        const stillHasMore =
          hasMoreBackwardsRef.current || Boolean(result?.hasMore);
        const containerNow = chatWrapperRef.current;
        const stillShort = !listOverflows(containerNow);
        const stillAtTop = containerNow ? isAtScrollTop(containerNow, TOP_PX) : false;
        if (loaded) {
          shortProbeCountRef.current = 0;
          topHistoryRetryCountRef.current = 0;
        } else if (!stillHasMore) {
          // Confirmed no more history — stop retry loops so the loader can end.
          shortProbeCountRef.current = SHORT_THREAD_FILL_ATTEMPTS;
          topHistoryRetryCountRef.current = TOP_HISTORY_RETRY_ATTEMPTS;
        } else if (stillShort) {
          shortProbeCountRef.current += 1;
          setOlderLoadTick((n) => n + 1);
        } else if (
          stillAtTop &&
          topHistoryRetryCountRef.current < TOP_HISTORY_RETRY_ATTEMPTS
        ) {
          // Noise-only raw pages — retry a bounded number of times while at top.
          topHistoryRetryCountRef.current += 1;
          setOlderLoadTick((n) => n + 1);
        }
      })
      .catch(() => {
        if (gen !== requestGenRef.current) return;
        if (!listOverflows(chatWrapperRef.current)) {
          shortProbeCountRef.current += 1;
          setOlderLoadTick((n) => n + 1);
        }
      })
      .finally(async () => {
        if (gen !== requestGenRef.current) return;
        loadOlderInFlightRef.current = false;
        const elapsed = Date.now() - startedAt;
        if (elapsed < MIN_HISTORY_LOADER_MS) {
          await waitMs(MIN_HISTORY_LOADER_MS - elapsed);
        }
        if (gen === requestGenRef.current) {
          setIsPagingUp(false);
        }
      });
    return true;
  }, [loadingOlder, isPagingUp, loadOlderMessages, chatWrapperRef, saveHistoryAnchor]);

  const handleChatScroll = useCallback(() => {
    if (scrollRafRef.current) return;

    scrollRafRef.current = window.requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const container = chatWrapperRef.current;
      if (!container || !hasDoneInitialScrollRef.current) return;

      const near = isNearBottom(container);
      nearBottomRef.current = near;

      if (!near) {
        stickToLatestUntilUserScrollRef.current = false;
      }

      if (isAtScrollTop(container, TOP_PX)) {
        requestOlderMessages();
      }
    });
  }, [requestOlderMessages, chatWrapperRef]);

  // Observe the history header row — fires when the user scrolls it into view.
  useEffect(() => {
    const root = chatWrapperRef.current;
    const target = historyTopRef?.current;
    if (!root || !target || !isMessageListPainted || !hasMoreBackwards) {
      return undefined;
    }
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!hasDoneInitialScrollRef.current) return;
        if (!hasMoreBackwardsRef.current) return;
        if (!entries[0]?.isIntersecting) return;
        requestOlderMessages();
      },
      { root, threshold: 0, rootMargin: '0px 80px 0px 0px' }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [
    chatWrapperRef,
    historyTopRef,
    isMessageListPainted,
    hasMoreBackwards,
    messagesLayoutFingerprint,
    requestOlderMessages,
    roomId,
    lastVisibleMessageKey,
  ]);

  // Restore scroll position after older messages are prepended above the viewport.
  useLayoutEffect(() => {
    if (!pendingHistoryAnchorRef.current) return;

    const container = chatWrapperRef.current;
    const before = historyAnchorMetricsRef.current;
    pendingHistoryAnchorRef.current = false;

    if (!container || !before || before.scrollHeight <= 0) return;

    preserveScrollAfterPrepend(container, before);
  }, [messagesLayoutFingerprint, chatWrapperRef]);

  useLayoutEffect(() => {
    const container = chatWrapperRef.current;
    if (!container) return;

    if (lastScrollRoomIdRef.current !== roomId) {
      lastScrollRoomIdRef.current = roomId;
      cancelPendingInitialScroll();
      hasDoneInitialScrollRef.current = false;
      requestGenRef.current += 1;
      loadOlderInFlightRef.current = false;
      setIsPagingUp(false);
      shortProbeCountRef.current = 0;
      topHistoryRetryCountRef.current = 0;
      pendingHistoryAnchorRef.current = false;
      historyAnchorMetricsRef.current = null;
      stickToLatestUntilUserScrollRef.current = true;
      nearBottomRef.current = true;
      prevListLayoutRef.current = { length: 0, firstKey: null };
      resizeObsHeightRef.current = 0;
    }

    if (!roomMessages.length || !isMessageListPainted) return;

    const firstMsg = roomMessages[0];
    const firstKey = firstMsg?.eventId || firstMsg?.id || String(firstMsg?.ts ?? '') || null;

    if (forceScrollToBottomRef.current) {
      forceScrollToBottomRef.current = false;
      scrollToBottom('smooth');
      hasDoneInitialScrollRef.current = true;
      stickToLatestUntilUserScrollRef.current = true;
      nearBottomRef.current = isNearBottom(container);
      prevListLayoutRef.current = { length: roomMessages.length, firstKey };
      return;
    }

    if (!hasDoneInitialScrollRef.current) {
      scrollToBottom('auto');
      const scrollGen = initialScrollGenRef.current;
      initialScrollRafRef.current = window.requestAnimationFrame(() => {
        initialScrollRafRef.current = window.requestAnimationFrame(() => {
          initialScrollRafRef.current = null;
          if (scrollGen !== initialScrollGenRef.current) return;
          const c = chatWrapperRef.current;
          if (!c || !stickToLatestUntilUserScrollRef.current) return;
          c.scrollTo({ top: c.scrollHeight, behavior: 'auto' });
          hasDoneInitialScrollRef.current = true;
          markAsReadManually?.();
          nearBottomRef.current = isNearBottom(c);
          prevListLayoutRef.current = { length: roomMessages.length, firstKey };
        });
      });
      return;
    }

    const nearBottom = isNearBottom(container);
    const firstKeyChanged =
      firstKey != null &&
      prevListLayoutRef.current.length > 0 &&
      firstKey !== prevListLayoutRef.current.firstKey;
    const readingHistory = !stickToLatestUntilUserScrollRef.current;

    if (nearBottom && !readingHistory && !firstKeyChanged) {
      scrollToBottom('smooth');
      markAsReadManually?.();
    }

    nearBottomRef.current = nearBottom;
    prevListLayoutRef.current = { length: roomMessages.length, firstKey };
  }, [
    roomId,
    lastVisibleMessageKey,
    messagesLayoutFingerprint,
    isMessageListPainted,
    scrollToBottom,
    markAsReadManually,
    chatWrapperRef,
    cancelPendingInitialScroll,
  ]);

  useEffect(() => {
    return () => {
      cancelPendingInitialScroll();
      if (scrollRafRef.current) {
        window.cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [cancelPendingInitialScroll]);

  useEffect(() => {
    const el = chatWrapperRef.current;
    const observe =
      el &&
      isChatReady &&
      !loadingMessages &&
      isMessageListPainted &&
      roomMessages.length > 0;

    if (!observe || typeof ResizeObserver === 'undefined') return undefined;

    const ro = new ResizeObserver(() => {
      const node = chatWrapperRef.current;
      if (!node) return;
      if (!stickToLatestUntilUserScrollRef.current || !hasDoneInitialScrollRef.current) return;

      const h = node.scrollHeight;
      if (h <= resizeObsHeightRef.current) return;
      resizeObsHeightRef.current = h;

      requestAnimationFrame(() => {
        const c = chatWrapperRef.current;
        if (!c || !stickToLatestUntilUserScrollRef.current) return;
        c.scrollTo({ top: c.scrollHeight, behavior: 'auto' });
        nearBottomRef.current = isNearBottom(c);
      });
    });

    resizeObsHeightRef.current = el.scrollHeight;
    ro.observe(el);
    return () => ro.disconnect();
  }, [chatWrapperRef, isChatReady, loadingMessages, isMessageListPainted, roomId, roomMessages.length]);

  // Short threads may not overflow — fill in the background until scrollable.
  // Also retry while the user stays pinned to the top and older history may remain
  // (IntersectionObserver does not re-fire when the sentinel stays intersecting).
  useEffect(() => {
    if (!isMessageListPainted || loadingOlder || isPagingUp) return;
    if (!hasDoneInitialScrollRef.current) return;
    if (!hasMoreBackwardsRef.current) return;
    const container = chatWrapperRef.current;
    if (!container) return;

    const overflowing = listOverflows(container);
    if (!overflowing) {
      if (shortProbeCountRef.current >= SHORT_THREAD_FILL_ATTEMPTS) return;
      requestOlderMessages();
      return;
    }

    if (
      isAtScrollTop(container, TOP_PX) &&
      topHistoryRetryCountRef.current > 0 &&
      topHistoryRetryCountRef.current < TOP_HISTORY_RETRY_ATTEMPTS
    ) {
      requestOlderMessages();
    }
  }, [
    isMessageListPainted,
    loadingOlder,
    isPagingUp,
    messagesLayoutFingerprint,
    olderLoadTick,
    requestOlderMessages,
    chatWrapperRef,
  ]);

  const isLoadingOlder = loadingOlder || isPagingUp;

  return {
    handleChatScroll,
    scrollToBottom,
    forceScrollToBottomRef,
    isLoadingOlder,
  };
}
